import { Prose } from '../../components/Prose'
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { ExerciseImage } from '../../components/ExerciseImage'
import {
  EQUIPMENT_LABEL, MAX_ADDED, groupByMuscle, muscleTr,
  type CatalogExercise, type Equipment,
} from '../../lib/program'

/**
 * "Hareket ekle" seçicisi — kasa göre gruplanmış.
 *
 * Program üreticisi hareket kalıbı diliyle konuşuyor (yatay itme, dikey çekme);
 * hareket ararken insan kas adı düşünüyor ("göğüs bir şey daha ekleyeyim").
 * Bu ekran o çeviriyi yapıyor: üstte kas grupları, altında o kasın birincil
 * olarak çalıştığı, elindeki ekipmanla yapılabilen hareketler.
 *
 * Zaten programda olanlar listede yok — aynı hareketi ikinci kez eklemek
 * hacim değil, karışıklık üretir.
 */
export function AddExercise({ catalog, equipment, inDay, count, limit, onPick, onClose }: {
  catalog: CatalogExercise[]
  equipment: string[]
  /** Bu günde hâlihazırda olan hareket slug'ları. */
  inDay: string[]
  /** Bu güne kaç hareket eklenmiş — sınır mesajı için. */
  count: number
  /** Üst sınır. Üretilen günde 4 ek hareket, elle kurulan günde 12 satır. */
  limit?: number
  onPick: (slug: string) => void
  onClose: () => void
}) {
  const [all, setAll] = useState(false)
  const [muscle, setMuscle] = useState<string | null>(null)
  const [q, setQ] = useState('')

  // Tüm katalog ancak istenince iniyor: 500+ hareket, anlatımları ve görsel
  // yollarıyla birlikte küçük bir dosya değil. Program üretici zaten
  // küratörlü setle çalışıyor, bu sorgu sadece bu ekran için.
  const full = useQuery({
    queryKey: ['exercises', 'all'],
    queryFn: () => api.exercisesAll(),
    enabled: all,
    staleTime: 5 * 60_000,
  })

  const source = all && full.data ? full.data : catalog

  const groups = useMemo(
    () => groupByMuscle(source, { equipment, exclude: inDay, curatedOnly: !all }),
    [source, equipment, inDay, all])

  const query = q.trim().toLocaleLowerCase('tr')
  const shown = useMemo(() => {
    if (query) {
      // Arama grupları aşıyor: "press" yazan biri hangi kasta olduğunu bilmiyor.
      const seen = new Set<string>()
      const hits = groups.flatMap((g) => g.items).filter((x) => {
        if (seen.has(x.slug)) return false
        const hit = x.nameEn.toLocaleLowerCase('tr').includes(query) ||
          x.nameTr.toLocaleLowerCase('tr').includes(query)
        if (hit) seen.add(x.slug)
        return hit
      })
      return [{ muscle: 'search', label: `"${q}" için ${hits.length} hareket`, items: hits }]
    }
    return muscle ? groups.filter((g) => g.muscle === muscle) : groups.slice(0, 1)
  }, [groups, muscle, query, q])

  const cap = limit ?? MAX_ADDED
  const atLimit = count >= cap

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--grid)', paddingTop: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <input
          type="search"
          placeholder="Hareket ara…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ flex: '1 1 200px', maxWidth: 280 }}
        />
        <button className={all ? 'primary' : ''} style={{ padding: '4px 10px', fontSize: 12.5 }}
                onClick={() => { setAll(!all); setMuscle(null) }}>
          {all ? 'Seçilmiş listeye dön' : 'Tüm katalog'}
        </button>
        <button style={{ padding: '4px 10px', fontSize: 12.5 }} onClick={onClose}>Kapat</button>
      </div>

      <Prose className="note" style={{ marginTop: 0 }}>
        {all
          ? full.isLoading
            ? 'Katalogun tamamı yükleniyor…'
            : `Katalogun tamamı — ${full.data?.length ?? 0} hareket. Anlatımların bir kısmı `
              + 'çevrilmedi ve zorluk süzgeci kapalı; seçtiğin hareketin seviyesi kartta yazıyor.'
          : 'Seçilmiş liste: kalıbı ve Türkçe anlatımı olan çekirdek hareketler. '
            + 'Aradığını bulamazsan "Tüm katalog"a geç.'}
      </Prose>

      {!query && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {groups.map((g, i) => {
            const active = muscle === null ? i === 0 : muscle === g.muscle
            return (
              <button
                key={g.muscle}
                className={active ? 'primary' : ''}
                style={{ padding: '4px 10px', fontSize: 12.5 }}
                onClick={() => setMuscle(g.muscle)}
              >
                {muscleTr(g.muscle)} <span style={{ opacity: 0.65 }}>({g.items.length})</span>
              </button>
            )
          })}
        </div>
      )}

      {atLimit && (
        <div className="warnbox" style={{ marginBottom: 10 }}>
          Bu güne {cap} hareket eklenmiş — sınır burada. Sınır fizyolojik değil,
          takvimsel: her ek hareket ~5 dakika ve bir noktadan sonra atlanan antrenman
          olarak geri dönüyor. Başka bir şey eklemek istiyorsan önce birini çıkar.
        </div>
      )}

      {shown.map((g) => (
        <div key={g.muscle}>
          {query && <h3 style={{ marginTop: 0 }}>{g.label}</h3>}
          {g.items.length === 0 && (
            <Prose className="note">Elindeki ekipmanla bu aramaya uyan hareket yok.</Prose>
          )}
          <div className="alt-grid">
            {g.items.map((x) => (
              <button key={x.slug} className="alt" disabled={atLimit} onClick={() => onPick(x.slug)}>
                <ExerciseImage images={x.images} pattern={x.pattern} />
                <span className="alt-name">{x.nameEn}</span>
                <span className="alt-sub">
                  {EQUIPMENT_LABEL[x.equipment as Equipment] ?? x.equipment}
                  {x.level === 'intermediate' && ' · orta seviye'}
                  {x.level === 'expert' && ' · ileri seviye'}
                  {x.primaryMuscles.length > 1 && ` · ${x.primaryMuscles.map(muscleTr).join(', ')}`}
                </span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
